import {
  Button,
  Checkbox,
  Col,
  Input,
  NettgravSpinner,
  Row,
  Select,
  Tooltip,
  WorthyDateField,
} from 'design-system';
import { Form } from 'design-system/compat';
import { BadgeCheck, Eye, EyeOff, Info as InfoCircleOutlined } from 'lucide-react';

import dayjs from 'dayjs';
import { clsx, showNotification } from 'design-system';
import isEmpty from 'lodash.isempty';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../../../analytics/consent';
import { getSafeReturnPath, makeReturnUrl } from '../../../lib/returnUrl';
import { getListOfCountries } from '../../../redux/actions';
import { CONSTANTS } from '../../../service/constants';
import { fetch_request } from '../../../service/request';
import BottomMessage from './BottomMessage';
import { beginRegistrationSubmission, buildSafeRegistrationDraft } from './registrationSafety';
import styles from './CreateMembership.module.css';

const PERSISTED_REGISTER_FORM_KEY = 'nettgrav-register-form';
const PERSISTED_FLAG_KEY = 'nettgrav-register-form-flag';
const SIGNUP_START_KEY = 'ng.signup.started_at';
const EMAIL_DOMAIN_FIXES = {
  'gmial.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outllok.com': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'live.con': 'live.com',
};
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  '10minutemail.com',
  '10minutemail.net',
  'guerrillamail.com',
  'mailinator.com',
  'tempmail.com',
  'throwawaymail.com',
]);

const getDomainCountryCode = () => {
  if (typeof window === 'undefined') return null;
  const host = (window.location.hostname || '').toLowerCase();
  if (host.endsWith('.no')) return 'NO';
  if (host.endsWith('.dk')) return 'DK';
  if (host.endsWith('.se')) return 'SE';
  if (host.endsWith('.fi')) return 'FI';
  return null;
};

const COUNTRY_CODE_ALIASES = {
  NO: ['NO', 'NOR'],
  DK: ['DK', 'DNK'],
  SE: ['SE', 'SWE'],
  FI: ['FI', 'FIN'],
};

const COUNTRY_NAME_ALIASES = {
  NO: ['NORWAY', 'NORGE'],
  DK: ['DENMARK', 'DANMARK'],
  SE: ['SWEDEN', 'SVERIGE'],
  FI: ['FINLAND', 'SUOMI'],
};

const findCountryByCode = (countries, code) => {
  const normalized = String(code || '')
    .trim()
    .toUpperCase();
  if (!normalized) return null;

  const targetCodes = COUNTRY_CODE_ALIASES[normalized] || [normalized];
  const targetNames = COUNTRY_NAME_ALIASES[normalized] || [];

  return (
    countries.find((item) => {
      const candidateCodes = [
        item?.country_code,
        item?.iso2,
        item?.iso3,
        item?.iso,
        item?.alpha2,
        item?.alpha3,
        item?.code,
      ]
        .map((value) =>
          String(value || '')
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean);
      const candidateName = String(item?.name || '')
        .trim()
        .toUpperCase();

      return (
        candidateCodes.some((value) => targetCodes.includes(value)) ||
        targetNames.includes(candidateName)
      );
    }) || null
  );
};

const InputForm = ({
  setHidePage,
  setUser,
  onSuccess,
  isJoinSheet = false,
  hideLocaleFields = false,
  defaultCountryCode,
  defaultLanguageKey,
  hideSubmitButton = false,
  onSubmitReady,
  onSubmitStateChange,
  onValuesChange,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const storedReturnUrl =
    typeof window !== 'undefined' ? window.localStorage.getItem('ng.returnUrl') : null;
  const returnUrl =
    getSafeReturnPath(searchParams.get('next')) ||
    getSafeReturnPath(searchParams.get('returnUrl')) ||
    getSafeReturnPath(storedReturnUrl) ||
    getSafeReturnPath(makeReturnUrl(location));
  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [infoKey, setInfoKey] = useState(null);
  const [disableEmail, setDisableEmail] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const submittingRef = useRef(false);
  const signupStartRef = useRef(null);
  const signupStartedTrackedRef = useRef(false);
  const clearCachedPersistedValues = useCallback(() => {
    persistedValuesRef.current = null;
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(PERSISTED_REGISTER_FORM_KEY);
    window.sessionStorage.removeItem(PERSISTED_FLAG_KEY);
  }, []);
  const initialPersistedValues = (() => {
    if (typeof window === 'undefined') return null;
    const shouldRestore = window.sessionStorage.getItem(PERSISTED_FLAG_KEY) === 'true';
    const raw = window.sessionStorage.getItem(PERSISTED_REGISTER_FORM_KEY);
    if (!shouldRestore || !raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return buildSafeRegistrationDraft(parsed);
      }
    } catch (error) {
      console.log(error);
    }
    return null;
  })();

  const persistedValuesRef = useRef(initialPersistedValues);
  const passwordWatch = Form.useWatch('password', form);
  const confirmPasswordWatch = Form.useWatch('confirmPassword', form);
  const emailWatch = Form.useWatch('email', form);
  const userInvitation = useSelector((state) => state.invitationToken.subscriptionInvitationData);
  const graveInvitation = useSelector((state) => state.grave.graveInvitationToken);

  const hasRestoredRef = useRef(false);

  const getCampaignContext = useCallback(() => {
    const source =
      searchParams.get('utm_source') ||
      searchParams.get('source') ||
      (returnUrl ? 'return_url' : 'direct');
    const campaign = searchParams.get('utm_campaign') || searchParams.get('campaign') || '';
    return { source, campaign };
  }, [returnUrl, searchParams]);

  useEffect(() => {
    if (signupStartRef.current) return;
    const now = Date.now();
    signupStartRef.current = now;
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(SIGNUP_START_KEY, String(now));
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (typeof onSubmitReady === 'function') {
      onSubmitReady(() => form.submit());
    }
  }, [onSubmitReady, form]);

  useEffect(() => {
    if (typeof onSubmitStateChange === 'function') {
      onSubmitStateChange(isCreatingAccount);
    }
  }, [isCreatingAccount, onSubmitStateChange]);

  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (typeof window === 'undefined') return;
    const shouldRestore = window.sessionStorage.getItem(PERSISTED_FLAG_KEY) === 'true';
    const raw = window.sessionStorage.getItem(PERSISTED_REGISTER_FORM_KEY);
    window.sessionStorage.removeItem(PERSISTED_REGISTER_FORM_KEY);
    window.sessionStorage.removeItem(PERSISTED_FLAG_KEY);
    let parsed = null;
    if (shouldRestore && raw) {
      try {
        const maybe = JSON.parse(raw);
        if (maybe && typeof maybe === 'object') {
          parsed = buildSafeRegistrationDraft(maybe);
        }
      } catch (error) {
        console.log(error);
      }
    }
    persistedValuesRef.current = parsed;
    form.resetFields();
    if (parsed) {
      form.setFieldsValue(parsed);
    }
    hasRestoredRef.current = true;
  }, [form]);

  useEffect(() => {
    if (!persistedValuesRef.current) return;
    const timer = setTimeout(() => {
      form.setFieldsValue(persistedValuesRef.current);
    }, 0);
    return () => clearTimeout(timer);
  }, [form]);

  const fetchLanguages = () => {
    fetch_request('/api/languages')
      .then((res) => res.json())
      .then((data) => setLanguages(data.languages))
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchCountries = async () => {
    await fetch_request('/api/countries')
      .then((response) => response.json())
      .then((data) => {
        setCountries(data.countries);
        dispatch(getListOfCountries(data));
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchCountries();
  }, [dispatch]);

  useEffect(() => {
    if (!countries.length) return;
    if (form.getFieldValue('country')) return;

    const domainCountryCode = getDomainCountryCode();
    if (!domainCountryCode) return;

    const matchedCountry = findCountryByCode(countries, domainCountryCode);
    if (matchedCountry?.id) {
      form.setFieldsValue({ country: matchedCountry.id });
    }
  }, [countries, form]);

  useEffect(() => {
    if (!hideLocaleFields) return;
    if (!countries.length || !languages.length) return;

    if (defaultCountryCode && !form.getFieldValue('country')) {
      const country = findCountryByCode(countries, defaultCountryCode);
      if (country?.id) {
        form.setFieldsValue({ country: country.id });
      }
    }
    if (!form.getFieldValue('country') && countries.length) {
      form.setFieldsValue({ country: countries[0]?.id });
    }

    if (defaultLanguageKey && !form.getFieldValue('language')) {
      const language = languages.find(
        (item) => String(item.key || '').toUpperCase() === defaultLanguageKey,
      );
      if (language?.id) {
        form.setFieldsValue({ language: language.id });
      }
    }
    if (!form.getFieldValue('language') && languages.length) {
      form.setFieldsValue({ language: languages[0]?.id });
    }
  }, [hideLocaleFields, countries, languages, defaultCountryCode, defaultLanguageKey, form]);

  const handleTermsClick = useCallback(() => {
    if (typeof window === 'undefined') return;
    const persistedValues = buildSafeRegistrationDraft(
      form.getFieldsValue(['email', 'userName', 'gender', 'country', 'birthday', 'language']),
    );
    try {
      const safeJSON = JSON.stringify(persistedValues, (_, value) => {
        if (value && typeof value === 'object' && value._isAMomentObject) {
          return value.toISOString();
        }
        return value;
      });
      window.sessionStorage.setItem(PERSISTED_REGISTER_FORM_KEY, safeJSON);
      window.sessionStorage.setItem(PERSISTED_FLAG_KEY, 'true');
    } catch (error) {
      console.log(error);
    }
  }, [form]);

  useEffect(() => {
    if (!isEmpty(userInvitation) && userInvitation.email) {
      form.setFieldsValue({
        email: userInvitation.email,
        userName: userInvitation.full_name,
      });
      setDisableEmail(true);
      setInfoKey('subscription_email_info');
    }
  }, [userInvitation]);

  useEffect(() => {
    if (
      !isEmpty(graveInvitation) &&
      graveInvitation.user?.full_name &&
      graveInvitation.user?.email
    ) {
      form.setFieldsValue({
        email: graveInvitation.user.email,
        userName: graveInvitation.user.full_name,
      });
      setDisableEmail(true);
      setInfoKey('grave_email_info');
    }
  }, [graveInvitation]);

  const currentYear = dayjs().year();
  const minDobYear = currentYear - 120;
  const maxDobYear = currentYear - 13;
  const genderOptions = [
    {
      value: 'mann',
      label: intl.formatMessage({ id: 'account_form_gender_option_male' }),
    },
    {
      value: 'kvinne',
      label: intl.formatMessage({ id: 'account_form_gender_option_female' }),
    },
    {
      value: 'annen',
      label: intl.formatMessage({ id: 'account_form_gender_option_other' }),
    },
  ];

  const getPasswordStatus = (fieldName) => {
    const value = form.getFieldValue(fieldName);
    const errors = form.getFieldError(fieldName);
    const touched = form.isFieldTouched(fieldName);

    if (fieldName === 'confirmPassword') {
      const passwordValue = form.getFieldValue('password');
      if (touched && value && passwordValue && value !== passwordValue) {
        return 'error';
      }
    }

    if (!touched && !value) return 'idle';
    if (errors && errors.length) return 'error';
    if (value) return 'success';
    return 'idle';
  };

  const getPasswordStatusClassName = (status) => {
    const classNames = [styles.passwordStatus];
    if (status === 'success') classNames.push(styles.passwordStatusSuccess);
    if (status === 'error') classNames.push(styles.passwordStatusError);
    if (status === 'idle') classNames.push(styles.passwordStatusHidden);
    return classNames.join(' ');
  };

  const getEmailWarning = useCallback(
    (rawValue) => {
      if (!rawValue) return null;
      const value = rawValue.trim().toLowerCase();
      if (!value.includes('@')) return null;
      const parts = value.split('@');
      if (parts.length !== 2) return null;
      const [local, domain] = parts;
      if (!local || !domain) return null;

      const suggestion = EMAIL_DOMAIN_FIXES[domain];
      if (suggestion) {
        return intl.formatMessage(
          {
            id: 'account_form_email_typo_warning',
            defaultMessage: 'Did you mean {suggestion}?',
          },
          { suggestion: `${local}@${suggestion}` },
        );
      }

      if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
        return intl.formatMessage({
          id: 'account_form_email_disposable_warning',
          defaultMessage:
            'Disposable email addresses sometimes fail. Consider using your main inbox.',
        });
      }

      return null;
    },
    [intl],
  );

  useEffect(() => {
    if (typeof Form.useWatch !== 'function') return;
    if (!form.isFieldTouched('confirmPassword')) return;
    form.validateFields(['confirmPassword']).catch(() => {});
  }, [passwordWatch, confirmPasswordWatch, form]);

  const toDobDayjs = (rawValue) => {
    if (!rawValue) return null;
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.slice(0, 10);
      return dayjs(trimmed, 'YYYY-MM-DD', true);
    }

    const { year, month, day } = rawValue || {};
    if (year && month && day) {
      const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
        day,
      ).padStart(2, '0')}`;
      return dayjs(iso, 'YYYY-MM-DD', true);
    }

    return null;
  };

  const onFinish = () => {
    if (!signupStartedTrackedRef.current) {
      signupStartedTrackedRef.current = true;
      const { source, campaign } = getCampaignContext();
      trackEvent('signup_started', {
        method: 'email',
        locale: intl.locale || '',
        source,
        campaign,
      });
    }
    form
      .validateFields()
      .then((values) => {
        if (values.password !== values.confirmPassword) {
          form.setFields([
            {
              name: 'confirmPassword',
              errors: [intl.formatMessage({ id: 'account_form_confirmPassword_match' })],
            },
          ]);
          return;
        }

        createUser(values);
      })
      .catch((info) => console.log('Validation Failed:', info));
  };

  const createUser = (value) => {
    if (!beginRegistrationSubmission(submittingRef)) return;
    setIsCreatingAccount(true);
    const dob = toDobDayjs(value.birthday);
    const payload = {
      user: {
        password: value.password,
        password_confirmation: value.confirmPassword,
        full_name: value.userName,
        gender: value.gender,
        email: value.email,
        country_id: value.country,
        date_of_birth: dob ? dob.format('YYYY-MM-DD') : '',
        preferred_language_attributes: {
          language_id: value.language,
        },
        agreed_terms: true,
      },
    };

    if (returnUrl) {
      payload.return_url = returnUrl;
    }

    if (userInvitation.length > 0) {
      payload.user['subscription_invitation_token'] = `${userInvitation.invitation_token}`;
    }

    if (!isEmpty(graveInvitation)) {
      payload.user['grave_invitation_token'] = `${graveInvitation.invitation_token}`;
    }

    const requestOptions = {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
      },
      redirect: 'follow',
    };

    fetch(`${CONSTANTS.baseUrl}/api/users.json`, requestOptions)
      .then(async (response) => {
        const result = await response.json();

        if (!response.ok) {
          const errorKey = result.error || result.code;
          const message = errorKey
            ? intl.formatMessage({ id: errorKey, defaultMessage: result.message || errorKey })
            : result.message;
          showNotification(message, 'error', 'auth');
          return;
        }

        if (result.user) {
          const now = Date.now();
          let startedAt = signupStartRef.current;
          try {
            if (typeof window !== 'undefined') {
              const stored = window.sessionStorage.getItem(SIGNUP_START_KEY);
              if (stored && Number(stored)) startedAt = Number(stored);
              window.sessionStorage.removeItem(SIGNUP_START_KEY);
              window.localStorage.setItem('ng.onboarding.started_at', String(now));
            }
          } catch {}
          const { source, campaign } = getCampaignContext();
          trackEvent('signup_completed', {
            user_id: result.user.id,
            method: 'email',
            locale: intl.locale || '',
            source,
            campaign,
            time_to_signup: startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : null,
          });
          trackEvent('registrering_fullfort', {
            user_id: result.user.id,
            method: 'email',
            locale: intl.locale || '',
            source,
            campaign,
          });
          if (!isEmpty(graveInvitation)) {
            trackEvent('invite_accepted', {
              grave_id: graveInvitation?.grave_id || graveInvitation?.grave?.id || null,
            });
          }
          setHidePage(true);
          setUser({
            name: result.user.full_name,
            email: result.user.email,
            id: result.user.id,
            slug: result.user.slug,
            confirmation_email_status: result.user.confirmation_email_status,
            confirmation_email_error_code: result.user.confirmation_email_error_code,
            confirmation_email_error_message: result.user.confirmation_email_error_message,
            confirmation_email_sent_at: result.user.confirmation_email_sent_at,
            confirmation_email_last_event_at: result.user.confirmation_email_last_event_at,
          });
          try {
            window.localStorage.removeItem('ng.returnUrl');
          } catch {}
          clearCachedPersistedValues();
          if (typeof onSuccess === 'function') {
            onSuccess(result.user);
          }
        } else {
          showNotification(result.message, 'warning', 'auth');
        }
      })
      .catch((error) => {
        showNotification(
          intl.formatMessage({
            id: 'registration_failed',
            defaultMessage: 'We could not create the account. Please try again.',
          }),
          'error',
          'auth',
        );
      })
      .finally(() => {
        submittingRef.current = false;
        setIsCreatingAccount(false);
      });
  };

  const ColumnSmall = isJoinSheet ? 24 : 12;

  return (
    <div>
      <Form
        form={form}
        name="userForm"
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        initialValues={persistedValuesRef.current || undefined}
        className={styles.form}
        aria-busy={isCreatingAccount}
      >
        <Row gutter={[16, 0]}>
          {/* Email */}
          <Col xs={24} sm={ColumnSmall}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const errors = form.getFieldError('email');
                const warning = getEmailWarning(emailWatch);
                const showWarning = !errors?.length && warning;
                return (
                  <Form.Item
                    label={
                      <span>
                        <FormattedMessage id="account_form_email_label" defaultMessage="Email" />{' '}
                        {disableEmail && infoKey && (
                          <Tooltip
                            title={<FormattedMessage id={infoKey || 'subscription_email_info'} />}
                          >
                            <InfoCircleOutlined />
                          </Tooltip>
                        )}
                      </span>
                    }
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({ id: 'account_form_email_required' }),
                      },
                      {
                        type: 'email',
                        message: intl.formatMessage({ id: 'account_form_email_invalid' }),
                      },
                    ]}
                    validateStatus={showWarning ? 'warning' : undefined}
                    help={showWarning ? warning : undefined}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: 'account_form_email_placeholder',
                      })}
                      disabled={disableEmail}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>

          {/* Username */}
          <Col xs={24} sm={ColumnSmall}>
            <Form.Item
              label={
                <FormattedMessage id="account_form_userName_label" defaultMessage="Username" />
              }
              name="userName"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'account_form_userName_required' }),
                },
                {
                  validator: (_, value) =>
                    !value || /\p{L}/u.test(value)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(intl.formatMessage({ id: 'account_form_userName_invalid' })),
                        ),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({ id: 'account_form_userName_placeholder' })}
              />
            </Form.Item>
          </Col>

          {/* Gender */}
          <Col xs={24} sm={ColumnSmall}>
            <Form.Item
              label={<FormattedMessage id="account_form_gender_label" defaultMessage="Gender" />}
              name="gender"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'account_form_gender_required' }),
                },
              ]}
            >
              <Select
                searchable={false}
                optionFilterProp="label"
                placeholder={intl.formatMessage({ id: 'placeholder_gender' })}
                options={genderOptions}
              />
            </Form.Item>
          </Col>

          {/* Country */}
          {!hideLocaleFields && (
            <Col xs={24} sm={ColumnSmall}>
              <Form.Item
                label={
                  <FormattedMessage id="account_form_country_label" defaultMessage="Country" />
                }
                name="country"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'account_form_country_required' }),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={intl.formatMessage({ id: 'account_form_country_placeholder' })}
                  options={countries.map((country) => ({
                    value: country.id,
                    label: country.name,
                  }))}
                ></Select>
              </Form.Item>
            </Col>
          )}

          {/* Date of Birth */}
          <Col xs={24} sm={ColumnSmall}>
            <WorthyDateField
              name="birthday"
              label={
                <FormattedMessage
                  id="account_form_dateOfBirth_label"
                  defaultMessage="Date of Birth"
                />
              }
              weekStartsOn={1}
              minYear={minDobYear}
              maxYear={maxDobYear}
              required
              showDate
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'account_form_dateOfBirth_required' }),
                },
                {
                  validator: (_, value) => {
                    const parsed = toDobDayjs(value);
                    if (!parsed || !parsed.isValid()) {
                      return Promise.reject(
                        new Error(intl.formatMessage({ id: 'account_form_dateOfBirth_required' })),
                      );
                    }

                    const age = dayjs().diff(parsed, 'year');
                    if (age < 13) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'account_form_dateOfBirth_underage',
                            defaultMessage: 'You must be at least 13 years old.',
                          }),
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Col>

          {/* Language */}
          {!hideLocaleFields && (
            <Col xs={24} sm={ColumnSmall}>
              <Form.Item
                label={
                  <FormattedMessage id="account_form_language_label" defaultMessage="Language" />
                }
                name="language"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'account_form_language_required' }),
                  },
                ]}
              >
                <Select
                  placeholder={intl.formatMessage({ id: 'account_form_language_placeholder' })}
                  options={languages.map((lang) => ({
                    value: lang.id,
                    label: lang.dialect,
                  }))}
                ></Select>
              </Form.Item>
            </Col>
          )}
          {/* Password */}
          <Col xs={24} sm={ColumnSmall}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const status = getPasswordStatus('password');
                const errors = form.getFieldError('password');
                const hasError = status === 'error' || (errors && errors.length > 0);
                return (
                  <Form.Item
                    label={
                      <FormattedMessage
                        id="account_form_password_label"
                        defaultMessage="Password"
                      />
                    }
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({ id: 'account_form_password_required' }),
                      },
                      {
                        min: 8,
                        message: intl.formatMessage({ id: 'account_form_password_min' }),
                      },
                      {
                        max: 128,
                        message: intl.formatMessage({ id: 'account_form_password_max' }),
                      },
                    ]}
                    validateTrigger={['onChange', 'onBlur']}
                    validateStatus={hasError ? 'error' : undefined}
                    help={hasError && errors?.length ? errors[0] : undefined}
                  >
                    <div
                      className={clsx(styles.passwordField, hasError && styles.passwordFieldError)}
                    >
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={intl.formatMessage({
                          id: 'account_form_password_placeholder',
                        })}
                        maxLength={128}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={intl.formatMessage({
                          id: showPassword ? 'auth_hide_password' : 'auth_show_password',
                          defaultMessage: showPassword ? 'Hide password' : 'Show password',
                        })}
                      >
                        {!showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <BadgeCheck
                        aria-hidden="true"
                        className={getPasswordStatusClassName(status)}
                        size={18}
                      />
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>

          {/* Confirm Password */}
          <Col xs={24} sm={ColumnSmall}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const status = getPasswordStatus('confirmPassword');
                const errors = form.getFieldError('confirmPassword');
                const passwordValue = form.getFieldValue('password');
                const confirmValue = form.getFieldValue('confirmPassword');
                const passwordsMismatch =
                  Boolean(confirmValue) && Boolean(passwordValue) && passwordValue !== confirmValue;
                const hasError = passwordsMismatch || (errors && errors.length > 0);
                const helpMessage =
                  hasError &&
                  (errors?.[0] ||
                    (passwordsMismatch
                      ? intl.formatMessage({ id: 'account_form_confirmPassword_match' })
                      : undefined));
                return (
                  <Form.Item
                    label={
                      <FormattedMessage
                        id="account_form_confirmPassword_label"
                        defaultMessage="Confirm Password"
                      />
                    }
                    name="confirmPassword"
                    dependencies={['password']}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'account_form_confirmPassword_required',
                        }),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({ id: 'account_form_confirmPassword_match' }),
                            ),
                          );
                        },
                      }),
                    ]}
                    validateStatus={hasError ? 'error' : undefined}
                    help={
                      helpMessage ? (
                        <span className={styles.passwordHelpMessage}>{helpMessage}</span>
                      ) : undefined
                    }
                  >
                    <div
                      className={clsx(styles.passwordField, hasError && styles.passwordFieldError)}
                    >
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={intl.formatMessage({
                          id: 'account_form_confirmPassword_placeholder',
                        })}
                        maxLength={128}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        aria-label={intl.formatMessage({
                          id: showConfirmPassword ? 'auth_hide_password' : 'auth_show_password',
                          defaultMessage: showConfirmPassword ? 'Hide password' : 'Show password',
                        })}
                      >
                        {!showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <BadgeCheck
                        aria-hidden="true"
                        className={getPasswordStatusClassName(status)}
                        size={18}
                      />
                    </div>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>

        {/* Checkbox (Last) */}
        <Form.Item
          name="checked"
          valuePropName="checked"
          className={styles.checkboxRow}
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(intl.formatMessage({ id: 'account_form_checked_required' })),
                    ),
            },
          ]}
        >
          <Checkbox className={styles.checkboxControl}>
            <FormattedMessage
              id="account_form_checked_label"
              defaultMessage="I agree to the {termsLink}"
              values={{
                termsLink: (
                  <Link to="/terms" className={styles.link} onClick={handleTermsClick}>
                    <FormattedMessage
                      id="auth_termsAndCondition"
                      defaultMessage="terms and conditions"
                    />
                  </Link>
                ),
              }}
            />
          </Checkbox>
        </Form.Item>

        {/* Submit Button */}
        {!hideSubmitButton && (
          <Form.Item className={styles.submitRow}>
            <Button
              variant="filled"
              color="primary"
              size="lg"
              htmlType="submit"
              block
              loading={isCreatingAccount}
              disabled={isCreatingAccount}
            >
              <FormattedMessage id="account_form_submit" defaultMessage="Sign up" />
            </Button>
          </Form.Item>
        )}
      </Form>
      {!isJoinSheet && (
        <BottomMessage
          messageKey="auth_login_here"
          url="/sign-in"
          textKey="auth_alreadyRegistered"
        />
      )}
      {isCreatingAccount && (
        <div className={styles.loadingOverlay} role="status" aria-live="assertive">
          <div className={styles.loadingContent}>
            <NettgravSpinner size={40} className="ng-spinner--brand" />
            <span className={styles.loadingMessage}>
              <FormattedMessage
                id="account_form_creating_account"
                defaultMessage="Creating account..."
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputForm;
